import {
  isDefinedArray,
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber
} from "et3-type-guards";
import queryString from "query-string";
import { memoize } from "lodash";
import orderBy from "lodash/orderBy";
import { computed, observable, runInAction } from "mobx";

import { apiCallGetAdminAuditedEvents } from "../../../api/auditor-api/api-call-get-admin-audited-events";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { TAuditEvent } from "../../../Types";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import i18n from "../../../constants/i18n";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../../helpers/et3-picker-data-helper";

class AuditedEventsStore {
  adminInput = observable.box<TEt3PickerSingleValue>({
    value: "",
    label: i18n.get("CHOOSE")
  });
  typeInputAuditedEvent = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "all"
  });
  typeInputAuditedIssue = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "all"
  });
  dateMinInput = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 1, daysAdded: 0 })
  );
  dateMaxInput = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  adminAuditedEvents = observable.box<TAuditEvent[]>([]);

  adminAuditedEventsOrdered = computed(() => {
    const orderedAuditedEvents: TAuditEvent[] = orderBy(
      this.adminAuditedEvents.get(),
      ["audited_on"],
      ["desc"]
    );
    const finalizedArray = orderedAuditedEvents.map((data) => {
      return {
        ...data,
        event_id: data.id,
        audited_extra_data: {
          auditedDataType: data.type,
          auditedExtraData: data.extra_data,
          auditedPageName: data.page_name
        },
        audited_events_reasons: data.reasons
      };
    });
    return finalizedArray;
  });

  setTypeInputAuditedEvent = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.typeInputAuditedEvent.set(type);
    });
  };

  setTypeInputAuditedIssue = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.typeInputAuditedIssue.set(type);
    });
  };

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const eventName = queryString.parse(search).eventName;
      const issueType = queryString.parse(search).issueType;
      const dateMin = queryString.parse(search).dateMin[0];
      const dateMax = queryString.parse(search).dateMax[0];
      const adminId = parseInt(`${queryString.parse(search).adminID}`, 10);
      if (isDefinedString(eventName) && isDefinedString(issueType)) {
        //@webTeam fix admin name
        getAuditedEventsStore().adminInput.set({
          label: "",
          value: adminId.toString()
        });
        getAuditedEventsStore().typeInputADAuditedEventSetVal(eventName);
        getAuditedEventsStore().typeInputADAuditedEventSetVal(issueType);
        getAuditedEventsStore().dateMinInput.set(new Date(dateMin));
        getAuditedEventsStore().dateMaxInput.set(new Date(dateMax));
        await getAuditedEventsStore().getAuditedEvents();
      }
    }
  };

  typeInputADAuditedEventSetVal = (eventName: string) => {
    switch (eventName) {
      case "approval_description":
        this.setTypeInputAuditedEvent({
          label: "Approve/Reject Description",
          value: "approval_description"
        });
        break;
      case "approval_peer_description":
        this.setTypeInputAuditedEvent({
          label: "Approve/Reject Peer Description",
          value: "approval_peer_description"
        });
        break;
      case "approval_picture":
        this.setTypeInputAuditedEvent({
          label: "Approve/Reject Pictures",
          value: "approval_picture"
        });
        break;
      case "report":
        this.setTypeInputAuditedEvent({
          label: "Report",
          value: "report"
        });
        break;
      case "investigation_cases":
        this.setTypeInputAuditedEvent({
          label: "Investigation Cases",
          value: "investigation_cases"
        });
        break;
      case "all":
        this.setTypeInputAuditedEvent({
          label: "All",
          value: "all"
        });
        break;
    }
  };

  typeInputADAuditedIssueSetVal = (issueType: string) => {
    switch (issueType) {
      case "no_issue":
        this.setTypeInputAuditedIssue({
          label: "No Issue",
          value: "no_issue"
        });
        break;
      case "minor":
        this.setTypeInputAuditedIssue({
          label: "Minor Issue",
          value: "minor"
        });
        break;
      case "major":
        this.setTypeInputAuditedIssue({
          label: "Major Issue",
          value: "major"
        });
        break;
      case "all":
        this.setTypeInputAuditedIssue({
          label: "All",
          value: "all"
        });
        break;
    }
  };

  getAuditedEvents = async () => {
    this.adminAuditedEvents.set([]);
    const definedAdminID = reducerToZeroIfUndefined(
      parseInt(this.adminInput.get().value, 10)
    );
    const type = this.typeInputAuditedEvent.get().value;

    if (!isPositiveNumber(definedAdminID)) {
      return;
    }
    getCoreUIStore().setIsLoading("AUDITOR_AUDITED_EVENTS", true);
    const { data, status, code } = await apiCallGetAdminAuditedEvents({
      adminId: definedAdminID,
      dateMin: this.dateMinInput.get(),
      dateMax: this.dateMaxInput.get(),
      eventType: type,
      issueType: this.typeInputAuditedIssue.get().value
    });
    getCoreUIStore().setIsLoading("AUDITOR_AUDITED_EVENTS", false);
    if (status === "OK") {
      if (!isNullOrUndefined(data) && isDefinedArray(data)) {
        this.adminAuditedEvents.set(data);
      } else {
        this.adminAuditedEvents.set([]);
        getCorePopUpStore().showAlertMessage("EVENTS_NOT_FOUND");
      }
    } else {
      this.adminAuditedEvents.set([]);
      getCorePopUpStore().showAlertMessage(code);
    }
  };
  setAdmin = (admin: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.adminInput.set(admin);
    });
  };
}

const getAuditedEventsStore = memoize(
  () => new AuditedEventsStore(),
  () => 1
);
export default getAuditedEventsStore;
